import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { v7 as uuidv7 } from 'uuid'
import { ChevronDown, Inbox, Maximize2, Minimize2, NotebookPen, RefreshCcw, X } from 'lucide-react'
import type { KeyboardEvent } from 'react'
import type { RecurrencePattern } from '@/lib/types/recurrence'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { AddTaskInput } from '@/routes/features/tasks/components/AddTaskInput'
import { ProjectComboBox } from '@/routes/features/tasks/components/ProjectComboBox'
import { useStatuses } from '@/routes/features/tasks/hooks/useWorkspaceStatuses'
import { useProjects } from '@/routes/features/tasks/hooks/useProjects'
import { useProject } from '@/lib/db/rxdb/hooks/entities/projects'
import { useProjectCycles } from '@/lib/db/rxdb/hooks/entities/cycles'
import { useCycleWorkItemMutations } from '@/lib/db/rxdb/hooks/entities/cycleWorkItems'
import { useMembers } from '@/lib/db/rxdb/hooks/entities/members'
import { useWorkItemTypesForProject } from '@/lib/db/rxdb/hooks/entities/work-item-types'
import { resolveDefaultWorkItemTypeId } from '@/lib/types/workItemType'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import {
  usePageMutations,
  usePersonalNoteContentMutations,
  useProjectPages,
  useTaskContentMutations,
  useTaskLabelMutations,
  useTaskMutations,
} from '@/lib/db/rxdb/hooks'
import {
  confirmUpload,
  getPresignedUploadUrl,
} from '@/lib/api/features/attachments'
import { createTaskWithLabels } from '@/lib/sync/taskActions'
import { TiptapRichTextEditor, buildPlainTextEditorStateJSON } from '@/components/editor/TiptapRichTextEditor'
import { generateInitialRank, generateRankBetween } from '@/lib/utils/lexorank'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { getPlatformShortcutKeys } from '@/lib/utils/keyboard'
import { cn } from '@/lib/utils'
import useLoginStore from '@/store/LoginStore'
import { useTimezone } from '@/store/UserPreferencesStore'
import { useProjectStatusMode } from '@/hooks/useProjectStatusMode'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { useAllLabels } from '@/lib/hooks/useLabels'
import { useAllLabelGroups } from '@/lib/hooks/useLabelGroups'
import { PROGRESS_TYPES } from '@/lib/types/subscription'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'
import { parseQuickAddTokens } from '@/routes/features/tasks/utils/quickAddTokens'
import { createChipClass } from '@/routes/features/tasks/utils/createChipClasses'
import { usePersonalNoteForDate } from '@/routes/features/notes/hooks/usePersonalNoteForDate'
import { getTodayDateString } from '@/routes/features/notes/utils/noteDateUtils'
import { appendToNoteDoc } from '@/routes/features/notes/utils/appendToNote'
import { textOf } from '@/routes/features/notes/utils/carryOverTodos'
import { noteHref, pageHref } from '@/components/app/search/entityHref'
import { getUploadContentType } from '@/lib/utils/fileMime'

type CreateKind = 'task' | 'doc' | 'note'
const CREATE_KINDS: Array<CreateKind> = ['task', 'doc', 'note']

interface QuickCreateTaskDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  defaultProjectId?: string | null
  /** Prefills the due date each time the dialog opens (e.g. a calendar day's "+"). */
  defaultDueDate?: Date | null
  /** Prefills the title each time the dialog opens (⌘K's "Create task"). */
  initialName?: string
}

/**
 * The global "+" (design 29a): a centred card that files a task, a doc or a
 * line in today's note. Where it goes sits top left and defaults to the page
 * you're on; typed tokens (25a) become the labelled chips under the title.
 */
export function QuickCreateTaskDialog({
  open,
  onOpenChange,
  defaultProjectId,
  defaultDueDate,
  initialName,
}: QuickCreateTaskDialogProps) {
  const { t } = useTranslation(['tasks', 'common'])
  const navigate = useNavigate()
  const [kind, setKind] = useState<CreateKind>('task')
  const [isExpanded, setIsExpanded] = useState(false)
  const [taskName, setTaskName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedPriority, setSelectedPriority] = useState(0)
  const [selectedStatusId, setSelectedStatusId] = useState<string | null>(null)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [selectedLabelIds, setSelectedLabelIds] = useState<Array<string>>([])
  const [selectedWorkItemTypeId, setSelectedWorkItemTypeId] = useState<string | null>(null)
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null)
  const [startDate, setStartDate] = useState<Date | undefined>()
  const [dueDate, setDueDate] = useState<Date | undefined>()
  const [isSomeday, setIsSomeday] = useState(false)
  const [isAllDay, setIsAllDay] = useState(true)
  const [reminderOffsetMinutes, setReminderOffsetMinutes] = useState<number | null>(null)
  const [recurrencePattern, setRecurrencePattern] = useState<RecurrencePattern | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<Array<File>>([])
  const [addMore, setAddMore] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [isSaving, setIsSaving] = useState(false)
  const taskInputRef = useRef<HTMLInputElement>(null)
  const textInputRef = useRef<HTMLInputElement>(null)

  const workspaceId = useLoginStore((state) => state.workspaceId)
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)
  const userId = useLoginStore((state) => state.userId)
  const timezone = useTimezone()
  const { isPersonal } = useWorkspaceMode()
  const { statusMode: projectStatusMode, isAdvanced } = useProjectStatusMode(selectedProjectId)
  const statusMode = isPersonal ? 'simple' as const : projectStatusMode

  const {
    all: { statuses },
  } = useStatuses()
  const { projects, defaultProject } = useProjects()
  const { labels } = useAllLabels(workspaceId)
  const { labelGroups } = useAllLabelGroups(workspaceId)
  const { members } = useMembers()
  const { createTask } = useTaskMutations()
  const { createTaskLabel } = useTaskLabelMutations()
  const { createTaskContent } = useTaskContentMutations()
  const { createCycleWorkItem } = useCycleWorkItemMutations()
  const { createPage } = usePageMutations()
  const { project: selectedProject } = useProject(selectedProjectId ?? undefined)
  const { pages: selectedProjectPages } = useProjectPages(selectedProjectId ?? undefined)
  const { workItemTypes: projectWorkItemTypes } = useWorkItemTypesForProject(selectedProjectId)
  const isWorkItemTypeEnabled =
    FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES && !isPersonal && (selectedProject?.isWorkItemTypeEnabled ?? false)
  const resolvedDefaultProjectId = defaultProjectId ?? defaultProject?.id ?? null

  const todayNoteDate = getTodayDateString(timezone)
  const todayNote = usePersonalNoteForDate('daily', kind === 'note' ? todayNoteDate : undefined)
  const { updatePersonalNoteContent } = usePersonalNoteContentMutations()

  // Set default status when statuses load
  useEffect(() => {
    if (statuses.length === 0) {
      setSelectedStatusId(null)
      return
    }

    if (!isAdvanced) {
      const backlogStatus = statuses.find(
        (status) => status.progressType === PROGRESS_TYPES.BACKLOG,
      )
      setSelectedStatusId(backlogStatus?.id ?? statuses[0]?.id ?? null)
    } else {
      setSelectedStatusId(statuses[0]?.id ?? null)
    }
  }, [statuses, isAdvanced])

  // Set defaults
  useEffect(() => {
    setSelectedProjectId(resolvedDefaultProjectId)
  }, [resolvedDefaultProjectId])

  useEffect(() => {
    setSelectedUserId(userId)
  }, [userId])

  useEffect(() => {
    setSelectedWorkItemTypeId(
      isWorkItemTypeEnabled
        ? resolveDefaultWorkItemTypeId(projectWorkItemTypes)
        : null,
    )
  }, [selectedProjectId, isWorkItemTypeEnabled, projectWorkItemTypes])

  // A cycle belongs to one project; moving the task drops it.
  useEffect(() => {
    setSelectedCycleId(null)
  }, [selectedProjectId])

  // Prefill the due date for callers that open the dialog on a given day.
  useEffect(() => {
    if (open && defaultDueDate) {
      setDueDate(defaultDueDate)
      setIsAllDay(true)
      setIsSomeday(false)
    }
  }, [open, defaultDueDate])

  useEffect(() => {
    if (open && initialName) {
      setKind('task')
      setTaskName(initialName)
    }
  }, [open, initialName])

  // Focus the title on open and whenever the kind changes.
  useEffect(() => {
    if (!open) return
    const input = kind === 'task' ? taskInputRef : textInputRef
    const handle = setTimeout(() => input.current?.focus(), 0)
    return () => clearTimeout(handle)
  }, [open, kind])

  const labelCandidates = useMemo(
    () => labels.filter((label) => !label.isDeleted),
    [labels],
  )
  // Only projects the picker offers tasks under (children of a space).
  const projectCandidates = useMemo(
    () => projects.filter((project) => !!project.parentProjectId),
    [projects],
  )
  const memberCandidates = useMemo(
    () =>
      isPersonal
        ? []
        : members.filter((member) => !member.isDeleted && !member.isSuperAgent),
    [members, isPersonal],
  )

  // Typed tokens fill the chips (designs 25a, 29a): "!high", "@me",
  // "@dana", "#label", "@project" and a trailing date like "tomorrow ".
  const handleTaskNameChange = useCallback(
    (value: string) => {
      const parsed = parseQuickAddTokens(value, {
        labels: labelCandidates,
        members: memberCandidates,
        projects: projectCandidates,
        parseDates: true,
      })
      if (parsed.priority !== undefined) setSelectedPriority(parsed.priority)
      if (parsed.assignMe && userId) setSelectedUserId(userId)
      if (parsed.assigneeUserId) setSelectedUserId(parsed.assigneeUserId)
      const parsedLabelIds = parsed.labelIds
      if (parsedLabelIds) {
        setSelectedLabelIds((current) => [
          ...current,
          ...parsedLabelIds.filter((id) => !current.includes(id)),
        ])
      }
      if (parsed.projectId) setSelectedProjectId(parsed.projectId)
      if (parsed.dueDate) {
        // A date without a time is all-day, stored end-of-day like the picker.
        const due = new Date(parsed.dueDate)
        if (!parsed.dueHasTime) due.setHours(23, 59, 0, 0)
        setDueDate(due)
        setIsAllDay(!parsed.dueHasTime)
        setIsSomeday(false)
      }
      setTaskName(parsed.name)
    },
    [userId, labelCandidates, memberCandidates, projectCandidates],
  )

  /** `keepPlace` (Create more) keeps where it goes and the cycle. */
  const resetForm = useCallback((keepPlace = false) => {
    setTaskName('')
    setDescription('')
    setSelectedPriority(0)
    if (!keepPlace) {
      setSelectedProjectId(resolvedDefaultProjectId)
      setSelectedCycleId(null)
    }
    setStartDate(undefined)
    setDueDate(undefined)
    setIsSomeday(false)
    setIsAllDay(true)
    setReminderOffsetMinutes(null)
    setRecurrencePattern(null)
    setSelectedLabelIds([])
    setSelectedFiles([])
    setSelectedUserId(userId)
    setSelectedWorkItemTypeId(null)
    setFormKey((prev) => prev + 1)
  }, [resolvedDefaultProjectId, userId])

  const close = useCallback(() => {
    onOpenChange(false)
    resetForm()
    setKind('task')
    setIsExpanded(false)
  }, [onOpenChange, resetForm])

  const createTaskHandler = useCallback(async () => {
    const title = taskName.trim()
    if (!title) {
      toast.error(t('tasks:quickCreate.nameRequired'))
      return
    }
    if (!workspaceId || !selectedStatusId || !userId) {
      logger.logError(
        new Error('Cannot create task: Missing required context'),
        LogCategory.TASK_CREATE,
        { workspaceId, selectedStatusId, userId },
      )
      return
    }

    try {
      const taskId = uuidv7()
      const sortOrder = generateInitialRank()
      const editorJson = description || buildPlainTextEditorStateJSON('')

      let plainText = ''
      try {
        const parsed = JSON.parse(editorJson)
        const textNode = parsed.content?.[0]?.content?.[0]
        plainText = textNode?.text || ''
      } catch {
        plainText = ''
      }

      const newTask = {
        id: taskId,
        name: title,
        description: editorJson,
        descriptionFormat: 'tiptap',
        descriptionLength: editorJson.length,
        descriptionPlainText: plainText || null,
        sortOrder,
        projectId: selectedProjectId,
        userId: selectedUserId ?? userId,
        startDate: startDate ? startDate.getTime() : null,
        startDateType: startDate ? 'datetime' : null,
        dueDate: dueDate ? dueDate.getTime() : null,
        dueDateType: dueDate ? 'datetime' : null,
        priority: selectedPriority,
        isSomeday,
        isAllDay,
        statusId: selectedStatusId,
        workItemTypeId: selectedWorkItemTypeId || null,
        parentId: null,
        statusName: '',
        statusColor: '#gray',
      }

      await createTaskWithLabels({
        task: newTask,
        labelIds: selectedLabelIds,
        createTask,
        createTaskLabel,
        createTaskContent,
        labels,
        labelGroups,
      })

      if (selectedCycleId) {
        await createCycleWorkItem({
          envId: workspaceId,
          cycleId: selectedCycleId,
          workItemId: taskId,
          statusIdAtClose: null,
          progressTypeAtClose: null,
          addedAt: Date.now(),
          sourceCycleId: null,
        })
      }

      if (selectedFiles.length > 0 && workspaceId) {
        void Promise.all(
          selectedFiles.map(async (file) => {
            try {
              const uploadContentType = getUploadContentType(file)
              const presignedData = await getPresignedUploadUrl(
                workspaceId,
                ENTITY_TYPES.WORK_ITEM,
                taskId,
                {
                  fileName: file.name,
                  fileType: uploadContentType,
                  fileSizeBytes: file.size,
                },
              )

              const uploadResponse = await fetch(presignedData.uploadUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': uploadContentType },
              })

              if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.statusText}`)
              }

              await confirmUpload(workspaceId, presignedData.attachmentId)
            } catch (error) {
              logger.logError(
                error instanceof Error
                  ? error
                  : new Error('Failed to upload attachment'),
                LogCategory.TASK_CREATE,
              )
            }
          }),
        )
      }

      if (addMore) {
        resetForm(true)
        setTimeout(() => taskInputRef.current?.focus(), 0)
      } else {
        close()
      }
    } catch (error) {
      logger.logError(
        error instanceof Error ? error : new Error('Failed to create task'),
        LogCategory.TASK_CREATE,
      )
    }
  }, [
    taskName,
    description,
    workspaceId,
    selectedStatusId,
    userId,
    selectedProjectId,
    selectedUserId,
    startDate,
    dueDate,
    selectedPriority,
    isSomeday,
    isAllDay,
    selectedLabelIds,
    selectedFiles,
    addMore,
    selectedWorkItemTypeId,
    selectedCycleId,
    createTask,
    createTaskLabel,
    createTaskContent,
    createCycleWorkItem,
    labels,
    labelGroups,
    resetForm,
    close,
    t,
  ])

  // Doc: files a titled page where it goes and opens it to write in.
  const createDocHandler = useCallback(async () => {
    if (!selectedProject || !workspaceSlug || isSaving) return
    setIsSaving(true)
    try {
      const slug = crypto.randomUUID().slice(0, 8)
      const lastSortOrder =
        (selectedProjectPages ?? [])
          .filter((page) => !page.isDeleted && !page.parentPageId && page.sortOrder)
          .map((page) => page.sortOrder)
          .sort()
          .at(-1) ?? null
      await createPage({
        projectId: selectedProject.id,
        parentPageId: null,
        title: taskName.trim(),
        slug,
        icon: null,
        iconType: null,
        sortOrder: generateRankBetween(lastSortOrder, null),
      })
      close()
      void navigate({ to: pageHref(workspaceSlug, selectedProject, slug) })
    } catch {
      toast.error(t('common:globalCreate.docFailed'))
    } finally {
      setIsSaving(false)
    }
  }, [selectedProject, workspaceSlug, isSaving, selectedProjectPages, createPage, taskName, close, navigate, t])

  // Note: adds the title (and anything under it) to the end of today's note.
  const addToNoteHandler = useCallback(async () => {
    if (!taskName.trim() || !workspaceSlug || isSaving) return
    setIsSaving(true)
    try {
      const noteId = todayNote.note?.id ?? (await todayNote.createNoteIfNeeded())
      if (!noteId) throw new Error('No note for today')
      const existing = todayNote.content
      const doc = appendToNoteDoc(existing?.content, taskName, description)
      const json = JSON.stringify(doc)
      const data = {
        content: json,
        format: 'tiptap',
        length: json.length,
        plainText: (doc.content ?? []).map(textOf).join('\n'),
        version: (existing?.version ?? 0) + 1,
      }
      if (existing) await updatePersonalNoteContent(existing.id, data)
      else await todayNote.createPersonalNoteContent({ personalNoteId: noteId, ...data })
      close()
      const href = noteHref(workspaceSlug, 'daily', todayNoteDate)
      toast.success(t('common:globalCreate.noteAdded'), {
        action: {
          label: t('common:globalCreate.openNote'),
          onClick: () => void navigate({ to: href }),
        },
      })
    } catch (error) {
      logger.logError(
        error instanceof Error ? error : new Error('Failed to add to note'),
        LogCategory.TASK_CREATE,
      )
      toast.error(t('common:globalCreate.noteFailed'))
    } finally {
      setIsSaving(false)
    }
  }, [taskName, description, workspaceSlug, isSaving, todayNote, updatePersonalNoteContent, close, todayNoteDate, navigate, t])

  const submit = () => {
    if (kind === 'task') void createTaskHandler()
    else if (kind === 'doc') void createDocHandler()
    else void addToNoteHandler()
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      submit()
    }
  }

  const handleDateChange = (type: 'start' | 'due', date: Date | undefined) => {
    if (type === 'start') {
      setStartDate(date)
      if (date && isSomeday) setIsSomeday(false)
    } else {
      setDueDate(date)
      if (date && isSomeday) setIsSomeday(false)
    }
  }

  const kindTitle = {
    task: t('common:globalCreate.newTask'),
    doc: t('common:globalCreate.newDoc'),
    note: t('common:globalCreate.newNote'),
  }[kind]
  const modKeys = getPlatformShortcutKeys('↵').join('')
  const docNeedsProject = kind === 'doc' && !selectedProject

  const destination =
    kind === 'note' ? (
      <span className="flex h-[26px] items-center gap-1.5 rounded-[7px] bg-background px-2 text-foreground">
        <NotebookPen className="size-3.5 text-muted-foreground" />
        {t('common:globalCreate.todaysNote')}
      </span>
    ) : (
      <ProjectComboBox
        projects={projects}
        selectedProjectId={selectedProjectId}
        onProjectChange={setSelectedProjectId}
        showInbox={kind === 'task'}
        fontSize="xs"
        buttonClassName="h-[26px] w-auto max-w-[260px] gap-1.5 rounded-[7px] border-0 bg-background px-2 text-[12.5px] font-normal text-foreground shadow-none hover:bg-accent"
        triggerProps={{
          title: t('common:globalCreate.destination'),
          'data-testid': 'quick-create-destination',
        }}
        triggerContent={
          <span className="flex min-w-0 items-center gap-1.5">
            {selectedProject ? (
              <ProjectIcon
                variant="badge"
                iconType={selectedProject.iconType}
                icon={selectedProject.icon}
                iconColor={selectedProject.iconColor}
                fallbackLabel={selectedProject.name}
                size={12}
              />
            ) : kind === 'task' ? (
              <Inbox className="size-3.5 text-muted-foreground" />
            ) : null}
            <span className="truncate">
              {selectedProject?.name ??
                (kind === 'task'
                  ? t('common:globalCreate.inbox')
                  : t('common:globalCreate.docNeedsProject'))}
            </span>
            <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
          </span>
        }
      />
    )

  const cycleChip =
    kind === 'task' && selectedProject?.isCyclesEnabled ? (
      <CycleChip
        projectId={selectedProject.id}
        selectedCycleId={selectedCycleId}
        onCycleChange={setSelectedCycleId}
      />
    ) : null

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (isOpen) onOpenChange(true)
        else close()
      }}
    >
      <DialogContent
        showCloseButton={false}
        data-testid="quick-create-dialog"
        className={cn(
          'top-[12vh] max-h-[80vh] max-w-[calc(100vw-2rem)] translate-y-0 gap-0 overflow-visible rounded-[14px] border-0 bg-card p-0 shadow-[0_40px_80px_-20px_rgb(31_29_26/0.45),0_0_0_1px_rgb(31_29_26/0.06)] dark:shadow-[0_40px_80px_-20px_rgb(0_0_0/0.7),0_0_0_1px_rgb(255_255_255/0.06)]',
          isExpanded ? 'sm:max-w-[880px]' : 'sm:max-w-[640px]',
          // Phones (design 36d): a sheet sitting on the keyboard. --kb is the
          // keyboard's overlap on iOS; Android resizes the page, so it's 0 there.
          'phone:inset-x-0 phone:top-auto phone:bottom-[var(--kb,0px)] phone:max-w-none! phone:translate-x-0 phone:rounded-b-none phone:rounded-t-2xl phone:max-h-[calc(100dvh-var(--kb,0px)-3rem)] phone:pb-[var(--safe-area-bottom)]',
        )}
      >
        <DialogTitle className="sr-only">{kindTitle}</DialogTitle>
        <DialogDescription className="sr-only">
          {t('tasks:quickCreate.description')}
        </DialogDescription>

        <div className="flex min-w-0 items-center gap-2 pl-4 pr-3.5 pt-3 text-[12.5px] text-muted-foreground">
          {destination}
          <span className="hidden text-input sm:inline" aria-hidden>/</span>
          <span className="hidden truncate font-mono text-[11px] sm:inline">{kindTitle}</span>
          <span className="flex-1" />
          <div
            role="tablist"
            aria-label={kindTitle}
            className="flex shrink-0 rounded-lg bg-background p-0.5 text-xs"
          >
            {CREATE_KINDS.map((value) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={kind === value}
                data-testid={`quick-create-kind-${value}`}
                onClick={() => setKind(value)}
                className={cn(
                  'rounded-md px-2.5 py-[3px] transition-colors',
                  kind === value
                    ? 'bg-card font-semibold text-foreground shadow-[0_1px_2px_rgb(31_29_26/0.1)]'
                    : 'hover:text-foreground',
                )}
              >
                {t(`common:globalCreate.kinds.${value}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setIsExpanded((value) => !value)}
            aria-label={isExpanded ? t('common:globalCreate.collapse') : t('common:globalCreate.expand')}
            title={isExpanded ? t('common:globalCreate.collapse') : t('common:globalCreate.expand')}
            className="hidden size-[26px] shrink-0 place-items-center rounded-[7px] hover:bg-accent hover:text-foreground sm:grid"
          >
            {isExpanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
          </button>
          <button
            type="button"
            onClick={close}
            aria-label={t('common:globalCreate.close')}
            title={t('common:globalCreate.close')}
            className="grid size-[26px] shrink-0 place-items-center rounded-[7px] hover:bg-accent hover:text-foreground"
          >
            <X className="size-[15px]" />
          </button>
        </div>

        {kind === 'task' ? (
          <AddTaskInput
            key={formKey}
            ref={taskInputRef}
            variant="modal"
            className="overflow-y-auto"
            inputClassName={isExpanded ? 'min-h-[240px]' : undefined}
            showDescription={true}
            nameValue={taskName}
            onNameChange={handleTaskNameChange}
            onKeyDown={handleKeyDown}
            enableNaturalLanguageDate={false}
            descriptionValue={description}
            onDescriptionChange={setDescription}
            placeholder={t('common:globalCreate.titlePlaceholder.task')}
            descriptionPlaceholder={t('common:globalCreate.descriptionPlaceholder')}
            startDate={startDate}
            dueDate={dueDate}
            onStartDateChange={(date) => handleDateChange('start', date)}
            onDueDateChange={(date) => handleDateChange('due', date)}
            reminderOffsetMinutes={reminderOffsetMinutes}
            onReminderChange={setReminderOffsetMinutes}
            recurrencePattern={recurrencePattern}
            onRecurrenceChange={setRecurrencePattern}
            projects={projects}
            selectedProjectId={selectedProjectId}
            onProjectChange={setSelectedProjectId}
            selectedPriority={selectedPriority}
            onPriorityChange={setSelectedPriority}
            selectedUserId={selectedUserId}
            onUserChange={setSelectedUserId}
            statuses={statuses}
            selectedStatusId={selectedStatusId}
            onStatusChange={setSelectedStatusId}
            availableLabels={labels}
            labelGroups={labelGroups}
            selectedLabelIds={selectedLabelIds}
            onLabelsChange={setSelectedLabelIds}
            selectedFiles={selectedFiles}
            onFilesChange={setSelectedFiles}
            isSomeday={isSomeday}
            onSomedayChange={setIsSomeday}
            isAllDay={isAllDay}
            onIsAllDayChange={setIsAllDay}
            addMore={addMore}
            onAddMoreChange={setAddMore}
            selectedWorkItemTypeId={selectedWorkItemTypeId}
            onWorkItemTypeChange={isWorkItemTypeEnabled ? setSelectedWorkItemTypeId : undefined}
            workItemTypeCount={isWorkItemTypeEnabled ? projectWorkItemTypes.length : 0}
            extraChips={cycleChip}
            onCreateClick={() => void createTaskHandler()}
            statusMode={statusMode}
          />
        ) : (
          <div
            className="flex min-h-0 flex-col"
            onKeyDown={(event) => {
              if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                submit()
              }
            }}
          >
            <div className="flex flex-col gap-2 px-[18px] pb-4 pt-4">
              <input
                ref={textInputRef}
                data-testid="quick-create-text-title"
                type="text"
                value={taskName}
                onChange={(event) => setTaskName(event.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={500}
                placeholder={t(`common:globalCreate.titlePlaceholder.${kind}`)}
                className="w-full min-w-0 bg-transparent text-[19px] font-semibold leading-[1.35] tracking-[-0.01em] text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none"
              />
              {kind === 'doc' ? (
                <p className="text-[14px] text-muted-foreground">
                  {docNeedsProject
                    ? t('common:globalCreate.docNeedsProject')
                    : t('common:globalCreate.docOpens')}
                </p>
              ) : (
                <TiptapRichTextEditor
                  key={formKey}
                  namespace="quick-create-note"
                  initialContent={description}
                  editable
                  placeholder={t('common:globalCreate.notePlaceholder')}
                  contentEditableClassName={cn('text-[14px] min-h-[22px] py-0', isExpanded && 'min-h-[240px]')}
                  placeholderClassName="text-[14px] top-0"
                  onContentChange={setDescription}
                  className="min-h-[24px]"
                />
              )}
            </div>
            <div className="flex items-center gap-2.5 border-t border-border/70 bg-chrome py-2.5 pl-4 pr-3 text-[12.5px] text-muted-foreground">
              <span className="flex-1" />
              <Button
                type="button"
                data-testid="quick-create-text-submit"
                onClick={submit}
                disabled={!taskName.trim() || docNeedsProject || isSaving}
                className="h-[30px] gap-2 rounded-lg pl-3 pr-2 text-[12.5px] font-medium"
              >
                {kind === 'doc'
                  ? t('common:globalCreate.create.doc')
                  : t('common:globalCreate.create.note')}
                <span className="font-mono text-[10.5px] opacity-60 phone:hidden">{modKeys}</span>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** The Cycle chip (29a): the project's upcoming and active cycles. */
function CycleChip({
  projectId,
  selectedCycleId,
  onCycleChange,
}: {
  projectId: string
  selectedCycleId: string | null
  onCycleChange: (cycleId: string | null) => void
}) {
  const { t } = useTranslation('common')
  const [open, setOpen] = useState(false)
  const { cycles } = useProjectCycles(projectId)
  const openCycles = useMemo(
    () =>
      (cycles ?? [])
        .filter((cycle) => !cycle.isDeleted && cycle.status !== 'completed')
        .sort((a, b) => a.startDate - b.startDate),
    [cycles],
  )
  if (openCycles.length === 0) return null
  const selected = openCycles.find((cycle) => cycle.id === selectedCycleId)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="quick-create-cycle"
          className={createChipClass(!!selected)}
        >
          <RefreshCcw className="size-3.5" />
          {selected
            ? t('globalCreate.chips.cycleNumber', { number: selected.number })
            : t('globalCreate.chips.cycle')}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              <CommandItem
                value="none"
                onSelect={() => {
                  onCycleChange(null)
                  setOpen(false)
                }}
                className="text-xs text-muted-foreground"
              >
                {t('globalCreate.chips.noCycle')}
              </CommandItem>
              {openCycles.map((cycle) => (
                <CommandItem
                  key={cycle.id}
                  value={cycle.id}
                  onSelect={() => {
                    onCycleChange(cycle.id)
                    setOpen(false)
                  }}
                  className="flex items-center gap-2 text-xs"
                >
                  <RefreshCcw className="size-3.5 text-muted-foreground" />
                  <span className="flex-1 truncate">
                    {t('globalCreate.chips.cycleNumber', { number: cycle.number })}
                    {cycle.name ? ` · ${cycle.name}` : ''}
                  </span>
                  {cycle.status === 'active' && (
                    <span className="rounded bg-brand-tint px-1.5 text-[10.5px] text-brand-tint-foreground">
                      {t('globalCreate.chips.active')}
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
